import { Component, inject, OnInit, signal } from '@angular/core';
import { AuthService, LoadingService, NotificationService, PaginationComponent } from "../../../../core";
import { FormsModule } from "@angular/forms";
import { JobCardService } from "../../services/job-card.service";
import { CommonModule, TitleCasePipe, UpperCasePipe } from "@angular/common";
import { DeliveryJobCardDTO, JobCardGetDTO } from "../../interfaces/job-card.entity";
import { catchError, of, tap } from "rxjs";
import { StatusBadgeComponent } from "../../../shared/components/status-badge/status-badge.component";
import { ModalService } from "../../../shared/services/modal.service";
import { JobOperationService } from 'src/app/modules/shared/services/job-operation.service';
import { JobDTO, GetJobOperationDTO, RescheduledHoldReasonDTO } from 'src/app/modules/shared/interface/jop-operation.entity';
import { DepartmentService } from 'src/app/modules/shared/services/department.service';
import { Department, Machine } from 'src/app/modules/shared/interface/department.entity';
import { MachineService } from 'src/app/modules/shared/services/machine.service';
import { OperationShiftService } from '../../services/operation-shift.service';
import { OperatorSummaryItemDTO } from '../../interfaces/operation-shift.entity';
import { GlobalDepartmentService } from 'src/app/core/services/global-department.service';
import { OperatorService } from 'src/app/modules/shared/services/operator.service';
import { OperatorsDTO } from 'src/app/modules/shared/interface/operator.entity';
import { FrappeIntegrationService } from 'src/app/modules/shared/services/frappe-integration.service';
import { WorkOrderFindDTO } from 'src/app/modules/shared/interface/frape-integration.entity';

@Component({
    selector: 'app-job-operation-view',
    imports: [
        FormsModule,
        PaginationComponent,
        TitleCasePipe,
        UpperCasePipe,
        StatusBadgeComponent,
        CommonModule,
    ],
    templateUrl: './job-operation-view.component.html',
    standalone: true,
    styleUrl: './job-operation-view.component.scss'
})
export class JobOperationViewComponent implements OnInit {

    private loading = inject(LoadingService);
    protected jobCardService = inject(JobCardService);
    private notification = inject(NotificationService);
    public modal = inject(ModalService);
    public jobOperationService = inject(JobOperationService);
    public departmentService = inject(DepartmentService);
    public machineService = inject(MachineService);
    public authService = inject(AuthService);
    public operationShiftService = inject(OperationShiftService);
    public globalDepartmentService = inject(GlobalDepartmentService);
    public operatorService = inject(OperatorService);
    public frappeIntegrationService = inject(FrappeIntegrationService);

    public jobStatuses: string[] = ['in progress', 'completed', 'hold'];
    protected jobCardGetDTOS = signal<JobCardGetDTO[]>([]);
    protected GetJobOperationDTO = signal<GetJobOperationDTO[]>([]);
    protected rescheduledHoldReason = signal<RescheduledHoldReasonDTO | null>(null);
    protected $$jobDTOS = signal<JobDTO[]>([]);
    protected readonly $$totalItems = signal(0);
    protected readonly $$pageNumber = signal(1);
    protected readonly $$itemsPerPage = signal(1);
    protected $$departmentDTO = signal<Department[]>([]);
    protected $$availableMachines = signal<Machine[]>([]);
    protected $$allMachines = signal<Machine[]>([]);
    protected Department = signal<Department[]>([]);
    protected DeliveryJobCardDTO = signal<DeliveryJobCardDTO[]>([]);
    protected operatorSummary = signal<OperatorSummaryItemDTO[]>([]);

    protected selectedStatus: string = "";
    protected statusChanged: boolean = false;
    departmentId = this.globalDepartmentService.selectedDepartmentId;
    machineId: number | null = null;
    availableMachines: Machine[] = [];
    estimateDate: string = '';
    estimateTime: number | null = null;
    selectedStage: string | null = null;
    selectedFinishingValue: string = '';
    operationQuantity: number | null = null;
    quantity: number | null = null;
    opQuantity: number | null = null;
    remainingImpressions: number | null = null;
    printedImpressions: number | null = null;
    isRework: boolean = false;
    isFinal: boolean = false;
    reprintReason: string = '';
    remarks: string = '';
    selectedYear = signal(new Date().getFullYear());
    selectedMonth = signal(new Date().getMonth());
    passCount: number | null = 1;
    /** Staging operator dropdown value (not yet confirmed) */
    ncrOperatorPickId: number | null = null;
    /** Staging reason input for the operator being added */
    ncrOperatorPickReason: string = '';
    ncrOperatorPickNumber: string = '';
    /** Confirmed list of NCR operator entries to send in the payload */
    ncrOperators: { operatorId: number; ncrReason: string; ncrNumber: string }[] = [];
    readonly reprintReasons: string[] = ['ncr', 'shortage'];
    protected $$operators = signal<OperatorsDTO[]>([]);
    protected workOrders = signal<WorkOrderFindDTO[]>([]);
    protected selectedWorkOrder: string | null = null;
    protected selectedGroupNo: string | null = null;

    protected searchParamsOp = signal({
        id: 2,
        year: this.selectedYear(),
        month: this.selectedMonth() + 1
    });

    protected searchParams = signal({
        pp_number: '',
        status: '',
        delivery_date: '',
        machine_id: -1,
        department_id: this.globalDepartmentService.getDepartmentId() ?? -1,
        page_number: 1,
        items_per_page: 10
    });

    protected searchParamsDelivery = signal({
        start_date: '',
        end_date: ''
    })

    protected editingDeliveryDateForJobId = signal<number | null>(null);
    protected tempDeliveryDate = signal<string>('');
    protected isUpdatingDeliveryDate = signal<boolean>(false);
    protected isUpdatingIsFinal = signal<boolean>(false);

    minDate: string = new Date().toISOString().split('T')[0];
    minDateTime: string = new Date().toISOString().slice(0, 16);
    showOperationChange = signal(false);
    hasSearched = signal(false);
    public minEndDate: string = '';

    ngOnInit(): void {
        const canChangeStatus = this.authService.hasContextPermission('job-operation', 'changeOperation');
        this.showOperationChange.set(canChangeStatus);

        const selectedData = localStorage.getItem('#selectedMachine');
        if (selectedData === null || selectedData === '') {
            this.modal.$$MachineSelector.set(true)
        }
        // console.log(selectedData)
        // this.selectedStatus = this.jobCardGetDTOS()[0]?.jobStatus;
        this.fetchDepartment();
        this.operationShiftService.getOperatorSummaryByJobOperation(28217).subscribe()
    }

    checkStatusChange(): void {
        this.statusChanged = this.selectedStatus !== this.jobCardGetDTOS()[0]?.jobStatus;
    }

    protected updateStatus(): void {

        this.jobCardService.update(this.jobCardGetDTOS()[0].id, this.selectedStatus).pipe(
            tap(() => {
                this.modal.$$openModal.set(false);
                this.notification.set({ type: 'success', message: 'Job status updated successfully!' });
                this.statusChanged = false;
                this.fetchOperationData();
                this.selectedStatus = '';
            }),
            catchError(error => {
                this.modal.$$openModal.set(false);
                this.notification.set({ type: 'error', message: 'Failed to update job status' });
                console.error(error);
                return of(null);
            })
        ).subscribe();
    }


    getDuration(startedDateTime: string | null, completedDateTime: string | null): string {
        if (!startedDateTime || !completedDateTime) {
            return '-';
        }

        const startDate = new Date(startedDateTime);
        const endDate = new Date(completedDateTime);

        const durationMs = endDate.getTime() - startDate.getTime();
        const hours = Math.floor(durationMs / (1000 * 60 * 60));
        const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);

        if (hours > 0) {
            return `${hours} hours ${minutes} minutes`;
        } else if (minutes > 0) {
            return `${minutes} minutes ${seconds} seconds`;
        } else {
            return `${seconds} seconds`;
        }
    }

    getCompletedMessege(estimateDateTime: string | null, completedDateTime: string | null): string {
        if (!estimateDateTime || !completedDateTime) {
            return '-';
        }

        if (estimateDateTime > completedDateTime) {
            return 'Completed early';
        }
        else if (estimateDateTime == completedDateTime) {
            return 'Completed on time'
        }
        else {
            return 'Completed Late'
        }
    }

    public onPageChange(pageNumber: number): void {
        const updatedParams = {
            ...this.searchParams(),
            page_number: Number(pageNumber)
        };
        this.searchParams.set(updatedParams);
        this.fetchOperationData();

    }

    protected onOpenModal(ID: number): void {
        this.modal.$$openModal.set(true);

        const canChangeStatus = this.authService.hasContextPermission('job-operation', 'changeOperation');
        this.showOperationChange.set(canChangeStatus);

        // Re-populate machines from the cached department list every time the modal opens.
        const currentDeptId = this.departmentId();
        if (currentDeptId) {
            const dept = this.$$departmentDTO().find(d => d.departmentId === currentDeptId);
            this.availableMachines = dept ? dept.machines : [];
        }

        this.jobCardService.getById(String(ID)).pipe(
            tap(response => {
                this.loading.set(false);
                this.jobCardGetDTOS.set([response.data]);

                const jobCard = this.jobCardGetDTOS()[0];
                const isFlexo = jobCard?.productType?.toLowerCase().includes('flexo');
                this.operationQuantity = isFlexo ? jobCard.reelLength : jobCard.numberOfImpressions;
                this.quantity = jobCard.jobQuantity

            }),
            catchError(error => {
                this.loading.set(false);
                this.notification.set({ type: 'error', message: `Failed to load Job Operation details` });
                console.error(error);
                return of(null);
            })
        ).subscribe();
    }

    /** Opens the dedicated Add Operation modal for the given job card ID */
    protected onOpenAddOperation(ID: number): void {
        this.modal.$$openAddOperation.set(true);
        this.workOrders.set([]);
        this.selectedWorkOrder = null;
        this.selectedGroupNo = null;
        this.selectedFinishingValue = '';

        // Repopulate machines from the cached department list
        const currentDeptId = this.departmentId();
        if (currentDeptId) {
            const dept = this.$$departmentDTO().find(d => d.departmentId === currentDeptId);
            this.availableMachines = dept ? dept.machines : [];
        }

        this.jobCardService.getById(String(ID)).pipe(
            tap(response => {
                this.loading.set(false);
                this.jobCardGetDTOS.set([response.data]);
                const jobCard = this.jobCardGetDTOS()[0];
                const isFlexo = jobCard?.productType?.toLowerCase().includes('flexo');
                this.operationQuantity = isFlexo ? jobCard.reelLength : jobCard.numberOfImpressions;
                this.quantity = jobCard.jobQuantity;
            }),
            catchError(error => {
                this.loading.set(false);
                this.notification.set({ type: 'error', message: `Failed to load Job Operation details` });
                console.error(error);
                return of(null);
            })
        ).subscribe();
    }

    hasBreakingInWorkOrders(): boolean {
        const jobCard = this.jobCardGetDTOS()[0];
        if (!jobCard || !jobCard.workOrders) return false;
        return jobCard.workOrders.some((wo: any) => wo.operation?.trim().toUpperCase() === 'BREAKING');
    }

    protected onOperationOpenModal(ID: number): void {
        this.modal.$$openOpeartion.set(true)
        this.rescheduledHoldReason.set(null);
        this.jobOperationService.getById(String(ID)).pipe(
            tap(response => {
                this.loading.set(false);
                this.GetJobOperationDTO.set([response.data]);
                this.loadOperatorSummary(ID);
                if (response.data.prevOperationId) {
                    this.jobOperationService.getRescheduledHoldReason(response.data.id).pipe(
                        tap(res => {
                            if (res.data) {
                                this.rescheduledHoldReason.set(res.data);
                            }
                        })
                    ).subscribe();
                }
                // console.log('GetJobOperationDTO',this.GetJobOperationDTO())
            }),
            catchError(error => {
                this.loading.set(false);
                this.notification.set({ type: 'error', message: `Failed to load Job Operation details` });
                console.error(error);
                return of(null);
            })
        ).subscribe();
    }

    protected loadOperatorSummary(jobOperationId: number): void {
        this.operationShiftService.getOperatorSummaryByJobOperation(jobOperationId).pipe(
            tap(response => {
                this.operatorSummary.set(response.data);
            }),
            catchError(error => {
                this.notification.set({
                    type: 'error',
                    message: 'Failed to load operator summary'
                });
                console.error(error);
                return of([]);
            })
        ).subscribe();
    }

    protected updateIsFinal(event: Event, operation?: GetJobOperationDTO): void {
        const targetOp = operation || this.GetJobOperationDTO()[0];
        if (!targetOp) return;

        const checkbox = event.target as HTMLInputElement;
        const newStatus = checkbox.checked;
        const opId = targetOp.id;

        this.isUpdatingIsFinal.set(true);
        this.jobOperationService.updateIsFinal(opId, newStatus).subscribe({
            next: () => {
                this.isUpdatingIsFinal.set(false);
                targetOp.isFinal = newStatus;
                this.GetJobOperationDTO.update(list =>
                    list.map(o => o.id === opId ? { ...o, isFinal: newStatus } : o)
                );
                this.$$jobDTOS.update(jobs =>
                    jobs.map(j => ({
                        ...j,
                        operations: (j.operations || []).map(o =>
                            o.jobOperationId === opId ? { ...o, isFinal: newStatus } : o
                        )
                    }))
                );
                this.notification.set({
                    type: 'success',
                    message: `Operation marked as ${newStatus ? 'Final' : 'Not Final'}.`
                });
            },
            error: (err) => {
                this.isUpdatingIsFinal.set(false);
                checkbox.checked = !newStatus;
                this.notification.set({
                    type: 'error',
                    message: 'Failed to update Final Operation status.'
                });
                console.error('Failed to update isFinal:', err);
            }
        });
    }

    // new added
    public fetchOperationData(): void {
        const params = this.searchParams();
        
        // If search criteria are empty, do not fetch all data
        if (!params.pp_number?.trim() && !params.status?.trim()) {
            this.$$jobDTOS.set([]);
            this.$$totalItems.set(0);
            this.$$pageNumber.set(1);
            this.hasSearched.set(false);
            return;
        }

        this.hasSearched.set(true);
        this.jobOperationService.find(params).pipe(
            tap(response => {
                this.$$jobDTOS.set(response.data.data);
                this.$$totalItems.set(response.data.totalItems);
                this.$$pageNumber.set(response.data.page);
                this.$$itemsPerPage.set(response.data.itemsPerPage);
            }),
            catchError(error => {
                this.notification.set({ type: 'error', message: `Failed to fetch Job Operation data` });
                console.error(error);
                return of(null);
            })
        ).subscribe();
    }

    public fetchDepartment(): void {
        this.departmentService.getAllDepartments().subscribe({
            next: (result) => {
                if (result.data && result.data.departments) {
                    const departments = result.data.departments;
                    this.$$departmentDTO.set(departments);

                    this.$$availableMachines.set([]);

                    const machines = departments.reduce((acc: Machine[], dept: Department) => {
                        return acc.concat(dept.machines);
                    }, []);
                    this.$$allMachines.set(machines);

                    if (this.departmentId()) {
                        this.onDepartmentChange(this.departmentId()!);
                    }
                }
            },
            error: (error) => {
                console.error('Error fetching departments:', error);
            }
        });
    }

    protected get(): void {
        this.loading.set(true)
        this.departmentService.getOperationsByDep(this.searchParamsOp()).pipe(
            tap(response => {
                this.loading.set(false);
                this.Department.set([response.data]);
                //    console.log(this.Department())
            }),
            catchError(error => {
                this.loading.set(false);
                this.notification.set({ type: 'error', message: `Failed to fetch Job Operation data` });
                console.error(error);
                return of(null);
            })
        ).subscribe();
    }

    onDepartmentChange(departmentId: number): void {
        const deptId = Number(departmentId);

        this.globalDepartmentService.setDepartmentId(deptId);

        this.searchParams.update(params => ({
            ...params,
            department_id: deptId,
            page_number: 1
        }));

        const selectedDepartment = this.$$departmentDTO()
            .find(dept => dept.departmentId === deptId);

        this.availableMachines = selectedDepartment
            ? selectedDepartment.machines
            : [];

        this.loadOperators();
    }

    loadOperators(): void {
        const id = this.departmentId();
        this.operatorService.getAll(id).subscribe({
            next: (result) => {
                if (result.data?.operators) {
                    this.$$operators.set(result.data.operators);
                }
            },
            error: (error) => {
                console.error('Error fetching operators:', error);
            }
        });
    }

    saveMachineDetails(): void {
        if (
            !this.selectedStage ||
            !this.estimateDate ||
            !this.machineId ||
            this.estimateTime == null ||
            this.operationQuantity == null
        ) {
            this.notification.set({
                type: 'error',
                message: 'Please fill in all required fields before saving.'
            });
            return;
        }

        // Validate rework fields if isRework is checked
        if (this.selectedStage === 'PRINTING' && this.isRework) {
            if (!this.reprintReason) {
                this.notification.set({ type: 'error', message: 'Please select a rework reason.' });
                return;
            }
            if (this.reprintReason === 'ncr') {
                if (this.ncrOperators.length === 0) {
                    this.notification.set({ type: 'error', message: 'Please add at least one NCR operator.' });
                    return;
                }
                if (this.ncrOperators.some(op => !op.ncrNumber || !op.ncrNumber.trim())) {
                    this.notification.set({ type: 'error', message: 'All NCR operators must have an NCR number.' });
                    return;
                }
            }
        }

        this.modal.$$openAddOperation.set(false);
        this.loading.set(true);
        const selectedWorkOrderObj = this.selectedWorkOrder
            ? this.jobCardGetDTOS()[0].workOrders?.find((w: any) => w.workOrderId === this.selectedWorkOrder)
            : this.jobCardGetDTOS()[0].workOrders?.find((w: any) => w.operation === this.selectedStage);

        const jobOperations = [{
            stage: this.selectedStage.trim(),
            sequence: this.jobCardGetDTOS()[0].jobOperations.length + 1,
            estimatedDateTime: this.formatNewDateTime(this.estimateDate),
            jobId: Number(this.jobCardGetDTOS()[0].id),
            machineId: Number(this.machineId),
            estimateTime: this.estimateTime,
            operationQty: this.opQuantity,
            isResorting: (this.selectedStage === 'SORTING' && this.isRework) ? 1 : 0,
            isRework: this.isRework ? 1 : 0,
            groupNo: this.selectedGroupNo,
            remarks: this.remarks,
            jobPassNumber: this.passCount,
            workOrderId: selectedWorkOrderObj ? selectedWorkOrderObj.workOrderId : null,
            workOrderName: selectedWorkOrderObj ? selectedWorkOrderObj.item : null,
            isReprint: (this.selectedStage === 'PRINTING' && this.isRework) ? 1 : 0,
            reprintReason: this.isRework && this.selectedStage === 'PRINTING' ? this.reprintReason : "",
            ncrOperators: (this.isRework && this.selectedStage === 'PRINTING' && this.reprintReason === 'ncr')
                ? [...this.ncrOperators]
                : [],
            isFinal: this.isFinal
        }]


        this.jobOperationService.create({ 
            jobOperations, 
            deliverDate: this.jobCardGetDTOS()[0].deliveryDate,
            isFinal: this.isFinal
        }).subscribe(
            response => {
                this.loading.set(false);
                this.notification.set({ type: 'success', message: `Job operation saved successfully.` });
                this.fetchOperationData();
                this.estimateDate = '';
                this.machineId = null;
                this.selectedStage = '';
                this.selectedFinishingValue = '';
                this.estimateTime = null;
                this.operationQuantity = null;
                this.remarks = '';
                this.isRework = false;
                this.isFinal = false;
                this.reprintReason = '';
                this.ncrOperatorPickId = null;
                this.ncrOperatorPickReason = '';
                this.ncrOperatorPickNumber = '';
                this.ncrOperators = [];
                this.workOrders.set([]);
                this.selectedWorkOrder = null;
                this.selectedGroupNo = null;
            },
            error => {
                this.modal.$$openAddOperation.set(false);
                this.loading.set(false);
                this.notification.set({ type: 'error', message: `Failed to save job operation` });
            }
        )

    }

    protected getOperationByDep(ID: number | null): void {
        this.departmentService.getById(String(ID)).pipe(
            tap(response => {
                this.loading.set(false);
                this.Department.set([response.data]);
                // console.log(this.Department())  
            }),

            catchError(error => {
                this.loading.set(false);
                this.notification.set({ type: 'error', message: `Failed to load Purchase Order details` });
                console.error(error);
                return of(null);
            })
        ).subscribe();
    }

    onMachineSelect(machineId: number) {
        machineId = Number(machineId);

        const selectedMachine = this.$$allMachines().find(m => m.machineId === machineId);
        // console.log(selectedMachine)

        if (selectedMachine) {
            const hourlyOutput = selectedMachine.hourlyOutput;
            const jobCard = this.jobCardGetDTOS()[0];
            const isFlexo = jobCard?.productType?.toLowerCase().includes('flexo');
            const quantity = isFlexo ? (jobCard?.reelLength || 0) : (jobCard?.numberOfImpressions || 0);

            if (hourlyOutput > 0 && quantity >= 0) {
                const additionalTime = isFlexo ? 1 : 0.75;
                const estimateTime = (((quantity / hourlyOutput) + additionalTime).toFixed(2));
                this.estimateTime = Number(estimateTime);
            } else {
                this.estimateTime = null;
            }

            this.machineService.getNextAvailableTime(machineId).pipe(
                tap(response => {
                    const nextAvailableTime = response.data.nextAvailableTime;
                    // console.log(nextAvailableTime);

                    // const minDateTime = new Date(this.minDateTime);
                    // const nextAvailableDateTime = new Date(nextAvailableTime);
                    // console.log(nextAvailableDateTime);


                    // if (nextAvailableDateTime < minDateTime) {
                    //     nextAvailableDateTime.setTime(minDateTime.getTime());
                    // }

                    this.estimateDate = this.formatNewDateTime(response.data.nextAvailableTime);
                }),
                catchError(error => {
                    this.notification.set({ type: 'error', message: `Failed to get next available time` });
                    console.error(error);
                    return of(null);
                })
            ).subscribe();
        }
    }

    formatNewDateTime(dateStr: string): string {
        return dateStr.slice(0, 16).replace('T', ' ');
    }


    closeModel() {
        this.modal.$$openModal.set(false);
        this.jobCardGetDTOS.set([]);
    }

    /** Close the Add Operation modal and reset its form state */
    closeAddOperation(): void {
        this.modal.$$openAddOperation.set(false);
        this.jobCardGetDTOS.set([]);
        this.estimateDate = '';
        this.estimateTime = null;
        this.selectedStage = null;
        this.selectedFinishingValue = '';
        this.machineId = null;
        this.opQuantity = null;
        this.passCount = 1;
        this.isRework = false;
        this.reprintReason = '';
        this.remarks = '';
        this.ncrOperatorPickId = null;
        this.ncrOperatorPickReason = '';
        this.ncrOperatorPickNumber = '';
        this.ncrOperators = [];
        this.workOrders.set([]);
        this.selectedWorkOrder = null;
        this.selectedGroupNo = null;
    }

    addNcrOperator(): void {
        if (!this.ncrOperatorPickId || !this.ncrOperatorPickReason.trim() || !this.ncrOperatorPickNumber.trim()) {
            this.notification.set({ type: 'error', message: 'Please select an operator, enter an NCR reason, and enter an NCR number.' });
            return;
        }
        if (this.ncrOperators.some(o => o.operatorId === this.ncrOperatorPickId)) {
            this.notification.set({ type: 'error', message: 'This operator has already been added.' });
            return;
        }
        this.ncrOperators = [
            ...this.ncrOperators,
            { 
                operatorId: this.ncrOperatorPickId, 
                ncrReason: this.ncrOperatorPickReason.trim(), 
                ncrNumber: this.ncrOperatorPickNumber.trim() 
            }
        ];
        this.ncrOperatorPickId = null;
        this.ncrOperatorPickReason = '';
        this.ncrOperatorPickNumber = '';
    }

    /** Remove an NCR operator entry by operatorId */
    removeNcrOperator(operatorId: number): void {
        this.ncrOperators = this.ncrOperators.filter(o => o.operatorId !== operatorId);
    }

    /** Resolve an operator name from the $$operators signal by ID */
    getNcrOperatorName(operatorId: number): string {
        return this.$$operators().find(o => o.operatorId === operatorId)?.operatorName ?? String(operatorId);
    }

    public downloadDeliveryReportCSV(): void {
        const { start_date, end_date } = this.searchParamsDelivery();

        if (!start_date || !end_date) {
            this.notification.set({ type: 'error', message: 'Please select both Start Date and End Date' });
            return;
        }

        this.jobCardService.findDeliveries(this.searchParamsDelivery()).pipe(
            tap(response => {
                const data = response.data;
                if (!data || data.length === 0) {
                    this.notification.set({ type: 'error', message: 'No data to download' });
                    return;
                }

                const headers = Object.keys(data[0]).filter(h => h !== 'id');
                const csvRows = [
                    headers.join(','),
                    ...data.map(row => headers.map(field => {
                        const value = row[field as keyof typeof row];
                        if (value === null || value === undefined) return '';
                        return `"${String(value).replace(/"/g, '""')}"`;
                    }).join(','))
                ];

                const csvContent = csvRows.join('\r\n');
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');

                a.download = `delivery_report_${start_date}_to_${end_date}.csv`;
                a.href = url;
                a.click();
                URL.revokeObjectURL(url);

                this.DeliveryJobCardDTO.set(data);
                this.closeReportDownload();
            }),
            catchError(error => {
                this.notification.set({ type: 'error', message: `Failed to get report data` });
                console.error(error);
                return of(null);
            })
        ).subscribe();
    }


    openDownloadReport() {
        this.modal.$$reportDownload.set(true)
    }

    closeReportDownload() {
        this.loading.set(false);
        this.modal.$$reportDownload.set(false);
        this.searchParamsDelivery().end_date = '';
        this.searchParamsDelivery().start_date = ''
    }

    onStartDateChange(): void {
        const { start_date } = this.searchParamsDelivery();
        this.minEndDate = start_date;
    }

    onFinishingChange(value: string): void {
        this.selectedFinishingValue = value;
        let stage = value;
        let workOrderId: string | null = null;
        if (value && value.includes(':::')) {
            const parts = value.split(':::');
            stage = parts[0];
            workOrderId = parts[1];
        }
        this.selectedWorkOrder = workOrderId;
        this.onStageChange(stage);
    }

    onStageChange(stage: string) {
        this.selectedStage = stage;

        console.log(stage);

        if (stage === 'SORTING' || stage == 'PASTING') {
            this.opQuantity = this.quantity;
        } else {
            this.opQuantity = this.operationQuantity;
        }

        const jobCard = this.jobCardGetDTOS()[0];
        const jobCardId = jobCard?.id || jobCard?.jobCardId;
        const jobNo = jobCard?.jobNumber || (jobCard as any)?.jobNo;

        if (stage) {
            
            if (jobCardId) {
                this.jobOperationService.getByJobCardId(jobCardId, this.selectedWorkOrder || undefined).subscribe({
                    next: (response) => {
                        console.log('Fetched Job Card Stage Details:', response);
                        const existingOps = response?.data || [];
                        const existingGroup = existingOps.find((op: any) => 
                            op.groupNo != null && op.groupNo !== '' && 
                            (!this.selectedWorkOrder || op.workOrderId === this.selectedWorkOrder)
                        ) || existingOps.find((op: any) => op.groupNo != null && op.groupNo !== '');

                        if (existingGroup) {
                            this.selectedGroupNo = existingGroup.groupNo;
                        } else {
                            if (this.selectedWorkOrder) {
                                this.selectedGroupNo = `GRP-${String(this.selectedWorkOrder).trim()}-01`;
                            } else {
                                const cleanJobNo = (jobNo || String(jobCardId)).trim();
                                this.selectedGroupNo = `GRP-${cleanJobNo}-01`;
                            }
                        }
                    },
                    error: (error) => {
                        console.error('Error fetching job card stage details:', error);
                        if (this.selectedWorkOrder) {
                            this.selectedGroupNo = `GRP-${String(this.selectedWorkOrder).trim()}-01`;
                        } else {
                            const cleanJobNo = (jobNo || String(jobCardId)).trim();
                            this.selectedGroupNo = `GRP-${cleanJobNo}-01`;
                        }
                    }
                });
            }
        }
    }

    protected getSortedOperations(operations: any[]) {
        if (!operations) return [];

        return [...operations].sort((a, b) => {

            if (a.operationStatus === 'pending') return 1;
            if (b.operationStatus === 'pending') return -1;

            const dateA = new Date(a.startedDateTime || 0).getTime();
            const dateB = new Date(b.startedDateTime || 0).getTime();

            return dateA - dateB;
        });
    }

    startEditingDeliveryDate(job: JobDTO) {
        this.editingDeliveryDateForJobId.set(job.jobId);
        this.tempDeliveryDate.set(job.finalizedDeliveryDate || job.deliveryDate);
    }

    cancelEditingDeliveryDate() {
        this.editingDeliveryDateForJobId.set(null);
        this.tempDeliveryDate.set('');
    }

    saveDeliveryDate(jobId: number) {
        const newDate = this.tempDeliveryDate();
        if (!newDate) {
            this.notification.set({ type: 'error', message: 'Please select a valid date.' });
            return;
        }
        
        this.isUpdatingDeliveryDate.set(true);
        this.jobCardService.updateDeliveryDate(jobId, newDate).pipe(
            tap(() => {
                this.notification.set({ type: 'success', message: 'Delivery date updated successfully!' });
                this.isUpdatingDeliveryDate.set(false);
                this.editingDeliveryDateForJobId.set(null);
                this.fetchOperationData();
            }),
            catchError(error => {
                this.notification.set({ type: 'error', message: 'Failed to update delivery date' });
                this.isUpdatingDeliveryDate.set(false);
                console.error(error);
                return of(null);
            })
        ).subscribe();
    }

}
